import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "@vue/composition-api";

export default function useProfileTagsVisibility(profileTags: () => string[]) {
  const profileTagsContainer = ref<HTMLElement>();
  const showAllProfileTags = ref(false);
  const visibleProfileTags = ref<string[]>([]);

  const hiddenProfileTagsCount = computed(() => profileTags().length - visibleProfileTags.value.length);

  const calculateVisibleProfileTags = async () => {
    const tags = profileTags();
    if (showAllProfileTags.value || !tags.length) {
      visibleProfileTags.value = tags;
      return;
    }
    if (!profileTagsContainer.value) return;

    const maxRows = 3;
    const tagHeight = 24;
    const gap = 4;
    const maxHeight = maxRows * tagHeight + gap * (maxRows - 1);

    visibleProfileTags.value = [];
    for (const tag of tags) {
      if (profileTagsContainer.value.scrollHeight > maxHeight && visibleProfileTags.value.length) {
        visibleProfileTags.value.pop();
        break;
      }
      visibleProfileTags.value.push(tag);
      await nextTick();
    }
  };

  const toggleProfileTags = () => {
    showAllProfileTags.value = !showAllProfileTags.value;
    calculateVisibleProfileTags();
  };

  const onResize = () => {
    if (!showAllProfileTags.value) calculateVisibleProfileTags();
  };

  watch(
    profileTags,
    () => {
      showAllProfileTags.value = false;
      calculateVisibleProfileTags();
    },
    { immediate: true, deep: true }
  );

  onMounted(() => {
    calculateVisibleProfileTags();
    window.addEventListener("resize", onResize, { passive: true });
  });

  onUnmounted(() => {
    window.removeEventListener("resize", onResize);
  });

  return {
    profileTagsContainer,
    showAllProfileTags,
    visibleProfileTags,
    hiddenProfileTagsCount,
    toggleProfileTags,
  };
}
